/**
 * Sequence graph helpers for the campaign wizard canvas.
 */

export const STEP_TYPES = {
  CONNECTION_REQUEST: "CONNECTION_REQUEST",
  MESSAGE: "MESSAGE",
  VOICE_NOTE: "VOICE_NOTE",
  IN_MAIL: "IN_MAIL",
  PROFILE_VIEW: "PROFILE_VIEW",
  FOLLOW: "FOLLOW",
  LIKE_POST: "LIKE_POST",
  WAIT: "WAIT",
  CONDITION: "CONDITION",
  END: "END",
};

export const CONDITION_TYPES = {
  CONNECTED: "CONNECTED",
  NOT_CONNECTED: "NOT_CONNECTED",
  ACCEPTED: "ACCEPTED",
  NOT_ACCEPTED: "NOT_ACCEPTED",
  REPLIED: "REPLIED",
  NO_REPLY: "NO_REPLY",
};

export const STEP_META = {
  CONNECTION_REQUEST: {
    label: "Send connection request",
    description: "Send a connection request to lead",
    color: "blue",
  },
  MESSAGE: {
    label: "Send message",
    description: "Send a LinkedIn message",
    color: "blue",
  },
  VOICE_NOTE: {
    label: "Voice note",
    description: "Send a LinkedIn voice note",
    color: "blue",
  },
  IN_MAIL: {
    label: "Send InMail",
    description: "Send a LinkedIn InMail",
    color: "blue",
  },
  PROFILE_VIEW: {
    label: "View profile",
    description: "Visit lead's profile",
    color: "blue",
  },
  FOLLOW: {
    label: "Follow profile",
    description: "Follow lead's profile",
    color: "blue",
  },
  LIKE_POST: {
    label: "Like post",
    description: "Engage with lead's post",
    color: "blue",
  },
  CONDITION: {
    label: "If connected",
    description: "Check if lead accepted connection",
    color: "green",
  },
  WAIT: {
    label: "Wait",
    description: "Delay before next step",
    color: "gray",
  },
  END: {
    label: "Sequence end",
    description: "End of sequence",
    color: "gray",
  },
};

export function createStepId() {
  return `step_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
}

export function createEmptySequence() {
  const startId = "start";
  const endId = createStepId();
  return [
    {
      id: startId,
      type: "START",
      label: "Sequence start",
      order: 0,
      nextStepId: null,
      positionX: 250,
      positionY: 0,
    },
    {
      id: endId,
      type: STEP_TYPES.END,
      label: "Sequence end",
      order: 1,
      positionX: 250,
      positionY: 160,
    },
  ];
}

export const MIN_DELAY_HOURS = 3;
export const MAX_DELAY_DAYS = 500;
const MS_PER_HOUR = 60 * 60 * 1000;
const MS_PER_DAY = 24 * MS_PER_HOUR;

/** Human delay amount ("1 day", "3 hours") or null when the step has no delay. */
export function formatDelayAmount(step) {
  if (!step?.delay || step.delay === 0) return null;
  const ms = resolveDelayMs(step.delay, step.delayUnit);
  if (!ms) return null;
  const days = ms / MS_PER_DAY;
  const hours = ms / MS_PER_HOUR;
  if (Number.isInteger(days) && days >= 1) {
    return `${days} ${days === 1 ? "day" : "days"}`;
  }
  if (Number.isInteger(hours) && hours >= 1) {
    return `${hours} ${hours === 1 ? "hour" : "hours"}`;
  }
  return `${step.delay} ${step.delayUnit || "hours"}`;
}

export function formatDelayLabel(step) {
  const amount = formatDelayAmount(step);
  return amount ? `Wait ${amount}, then` : "No delay";
}

/**
 * Convert delay + unit to ms.
 * Templates historically stored ms while still setting delayUnit — detect that.
 */
export function resolveDelayMs(delay, delayUnit) {
  const amount = Number(delay);
  if (!Number.isFinite(amount) || amount <= 0) return 0;
  // Already milliseconds (legacy templates): e.g. 86400000 + unit "days"
  if (
    amount >= MS_PER_HOUR
    && (delayUnit === "hours" || delayUnit === "days" || delayUnit === "weeks")
  ) {
    return amount;
  }
  const unit = delayUnit || "hours";
  if (unit === "weeks") return amount * 7 * MS_PER_DAY;
  if (unit === "days") return amount * MS_PER_DAY;
  if (unit === "hours") return amount * MS_PER_HOUR;
  return amount;
}

/**
 * Canonical human delay for API: `{ delay: 5, delayUnit: 'days' }`.
 * Undoes legacy ms-encoded template values so prod validation doesn't multiply twice.
 */
export function normalizeDelayForApi(delay, delayUnit) {
  const ms = resolveDelayMs(delay, delayUnit);
  if (!ms) {
    return { delay: 0, delayUnit: "hours" };
  }
  const weeks = ms / (7 * MS_PER_DAY);
  if (Number.isInteger(weeks) && weeks >= 1) {
    return { delay: weeks, delayUnit: "weeks" };
  }
  const days = ms / MS_PER_DAY;
  if (Number.isInteger(days) && days >= 1) {
    return { delay: days, delayUnit: "days" };
  }
  const hours = ms / MS_PER_HOUR;
  if (Number.isInteger(hours) && hours >= 1) {
    return { delay: hours, delayUnit: "hours" };
  }
  return {
    delay: Math.max(1, Math.round(hours)),
    delayUnit: "hours",
  };
}

export function validateStepsClient(steps) {
  const errors = [];
  const actionTypes = new Set([
    STEP_TYPES.CONNECTION_REQUEST,
    STEP_TYPES.MESSAGE,
    STEP_TYPES.VOICE_NOTE,
    STEP_TYPES.IN_MAIL,
    STEP_TYPES.PROFILE_VIEW,
    STEP_TYPES.FOLLOW,
    STEP_TYPES.LIKE_POST,
    STEP_TYPES.WAIT,
  ]);
  const minMs = MIN_DELAY_HOURS * MS_PER_HOUR;
  const maxMs = MAX_DELAY_DAYS * MS_PER_DAY;

  for (const step of steps || []) {
    if (!actionTypes.has(step.type) || !step.delay || step.delay === 0) continue;
    const ms = resolveDelayMs(step.delay, step.delayUnit);
    if (!ms) continue;
    if (ms < minMs || ms > maxMs) {
      errors.push({
        stepId: step.id,
        message: `Delay must be between ${MIN_DELAY_HOURS}h and ${MAX_DELAY_DAYS} days`,
      });
    }
  }
  return { valid: errors.length === 0, errors };
}

export function stepsToFlow(steps, stepStats = []) {
  const statsById = new Map(
    (stepStats || []).map((s) => [s.stepId || String(s.stepIndex), s]),
  );
  const nodes = (steps || []).map((step, index) => {
    const stats = statsById.get(step.id) || statsById.get(String(index));
    return {
      id: step.id || `idx_${index}`,
      type: step.type === "START" || step.type === STEP_TYPES.END ? "terminal" : "action",
      position: {
        x: typeof step.positionX === "number" ? step.positionX : 200,
        y: typeof step.positionY === "number" ? step.positionY : index * 140,
      },
      data: {
        step,
        badge: stats ? stats.completed : undefined,
        label: formatStepCanvasLabel(step),
        description: STEP_META[step.type]?.description || "",
        delayLabel: step.type === "START" || step.type === STEP_TYPES.END
          ? null
          : formatDelayLabel(step),
      },
    };
  });

  const edges = [];
  for (const step of steps || []) {
    if (step.branches?.length) {
      for (const branch of step.branches) {
        const isPositive = ["ACCEPTED", "CONNECTED", "REPLIED", "OPENED"].includes(
          branch.condition,
        );
        edges.push({
          id: `${step.id}-${branch.targetStepId}-${branch.condition}`,
          source: step.id,
          target: branch.targetStepId,
          type: "smoothstep",
          label: branch.condition
            .replace(/_/g, " ")
            .toLowerCase()
            .replace(/^\w/, (c) => c.toUpperCase()),
          labelStyle: { fill: isPositive ? "#059669" : "#dc2626", fontSize: 11 },
          style: { stroke: isPositive ? "#34d399" : "#f87171" },
          data: { condition: branch.condition },
        });
      }
    } else if (step.nextStepId) {
      edges.push({
        id: `${step.id}-${step.nextStepId}`,
        source: step.id,
        target: step.nextStepId,
        type: "smoothstep",
        style: { stroke: "#94a3b8" },
      });
    }
  }
  return { nodes, edges };
}

export function flowToSteps(nodes, edges) {
  const steps = nodes.map((node, index) => {
    const step = { ...(node.data?.step || {}) };
    step.id = node.id;
    step.positionX = Math.round(node.position.x);
    step.positionY = Math.round(node.position.y);
    step.order = index;
    if (node.data?.step?.type) step.type = node.data.step.type;
    return step;
  });

  const byId = new Map(steps.map((s) => [s.id, s]));
  for (const step of steps) {
    step.branches = undefined;
    step.nextStepId = undefined;
  }
  for (const edge of edges) {
    const source = byId.get(edge.source);
    if (!source) continue;
    if (edge.data?.condition) {
      source.branches = source.branches || [];
      source.branches.push({
        condition: edge.data.condition,
        targetStepId: edge.target,
      });
    } else {
      source.nextStepId = edge.target;
    }
  }
  return steps.filter((s) => s.type !== "START" || true);
}

export function insertStepAfter(steps, afterId, newStep) {
  const list = [...steps];
  const after = list.find((s) => s.id === afterId);
  const endStep = list.find((s) => s.type === STEP_TYPES.END);
  const step = {
    ...newStep,
    id: newStep.id || createStepId(),
    order: list.length,
    nextStepId: after?.nextStepId || endStep?.id,
    delay: newStep.delay ?? 0,
    delayUnit: newStep.delayUnit || "hours",
  };
  if (after) {
    after.nextStepId = step.id;
  }
  // Insert before END if present
  const endIndex = list.findIndex((s) => s.type === STEP_TYPES.END);
  if (endIndex >= 0) {
    list.splice(endIndex, 0, step);
  } else {
    list.push(step);
  }
  return list.map((s, i) => ({ ...s, order: i }));
}

export function formatVoiceDurationLabel(durationMs) {
  const totalSec = Math.max(0, Math.round(Number(durationMs) / 1000) || 0);
  const minutes = Math.floor(totalSec / 60);
  const seconds = totalSec % 60;
  return `${minutes}:${String(seconds).padStart(2, "0")}`;
}

export function formatStepCanvasLabel(step) {
  if (step?.type === STEP_TYPES.VOICE_NOTE && step.voiceDurationMs) {
    return `Voice note · ${formatVoiceDurationLabel(step.voiceDurationMs)}`;
  }
  return step?.label || STEP_META[step?.type]?.label || step?.type || "";
}

export function defaultBranchesFor(type) {
  if (type === STEP_TYPES.CONNECTION_REQUEST) {
    return [
      { condition: CONDITION_TYPES.ACCEPTED, targetStepId: null },
      { condition: CONDITION_TYPES.NOT_ACCEPTED, targetStepId: null },
    ];
  }
  if (
    type === STEP_TYPES.MESSAGE
    || type === STEP_TYPES.IN_MAIL
    || type === STEP_TYPES.VOICE_NOTE
  ) {
    return [
      { condition: CONDITION_TYPES.REPLIED, targetStepId: null },
      { condition: CONDITION_TYPES.NO_REPLY, targetStepId: null },
    ];
  }
  if (type === STEP_TYPES.CONDITION) {
    return [
      { condition: CONDITION_TYPES.CONNECTED, targetStepId: null },
      { condition: CONDITION_TYPES.NOT_CONNECTED, targetStepId: null },
    ];
  }
  return [];
}

/** Fields accepted by CreateSequenceDto / SequenceStepDto (forbidNonWhitelisted). */
const SEQUENCE_STEP_API_FIELDS = [
  "id",
  "type",
  "label",
  "content",
  "useAiPersonalization",
  "messageVariants",
  "fallbackMessage",
  "fallbackContent",
  "voiceFileId",
  "voiceDurationMs",
  "voiceMimeType",
  "voiceOriginalName",
  "externalReference",
  "reactionType",
  "postsNewerThanDays",
  "skipDelayIfCannotLike",
  "fallbackUsedCount",
  "fallbackTotalCount",
  "withdrawAfterDays",
  "delay",
  "delayUnit",
  "linkedinAccountId",
  "order",
  "branches",
  "nextStepId",
  "positionX",
  "positionY",
];

/**
 * Strip UI-only fields and invalid branches before POST/PATCH /sequences.
 */
export function sanitizeSequenceStepForApi(step, order) {
  const out = { type: step.type, order };
  for (const key of SEQUENCE_STEP_API_FIELDS) {
    if (key === "type" || key === "order") continue;
    const value = step[key];
    if (value === undefined || value === null || value === "") continue;
    if (typeof value === "number" && Number.isNaN(value)) continue;
    out[key] = value;
  }
  if (Array.isArray(out.branches)) {
    out.branches = out.branches.filter(
      (branch) =>
        branch
        && typeof branch.condition === "string"
        && typeof branch.targetStepId === "string"
        && branch.targetStepId.length > 0,
    );
    if (out.branches.length === 0) {
      delete out.branches;
    }
  }
  if (out.delay !== undefined && out.delay !== null) {
    const normalized = normalizeDelayForApi(out.delay, out.delayUnit);
    out.delay = normalized.delay;
    out.delayUnit = normalized.delayUnit;
  } else if (out.delayUnit && !["hours", "days", "weeks"].includes(out.delayUnit)) {
    delete out.delayUnit;
  }
  return out;
}

/** Normalize step delays after importing a (possibly legacy) template. */
export function normalizeStepsDelays(steps) {
  return (steps || []).map((step) => {
    if (step.delay === undefined || step.delay === null || step.delay === 0) {
      return step;
    }
    const normalized = normalizeDelayForApi(step.delay, step.delayUnit);
    return { ...step, delay: normalized.delay, delayUnit: normalized.delayUnit };
  });
}

/* ------------------------------------------------------------------------ */
/* Tree builder helpers                                                      */
/*                                                                           */
/* A "slot" is an outgoing connection of a step: `{ parentId, condition }`.  */
/* `condition` is null for the linear `nextStepId`, or a branch condition.   */
/* A slot pointing at nothing is an open path the user can extend or end.    */
/* ------------------------------------------------------------------------ */

export const BRANCH_META = {
  ACCEPTED: { label: "Accepted", positive: true },
  NOT_ACCEPTED: { label: "Not accepted yet", positive: false },
  CONNECTED: { label: "Connected", positive: true },
  NOT_CONNECTED: { label: "Not connected", positive: false },
  REPLIED: { label: "Replied", positive: true },
  NO_REPLY: { label: "No reply yet", positive: false },
  OPENED: { label: "Opened", positive: true },
};

export function branchMeta(condition) {
  return (
    BRANCH_META[condition] || {
      label: String(condition || "")
        .replace(/_/g, " ")
        .toLowerCase()
        .replace(/^\w/, (c) => c.toUpperCase()),
      positive: false,
    }
  );
}

export function isTerminalStep(step) {
  return step?.type === "START" || step?.type === STEP_TYPES.END;
}

/** Negative paths on the left, positive on the right. */
export function sortBranchesForDisplay(branches) {
  return [...(branches || [])].sort(
    (a, b) => Number(branchMeta(a.condition).positive) - Number(branchMeta(b.condition).positive),
  );
}

/**
 * Branch that inherits the existing continuation when a branching step is inserted
 * mid-flow, and the one that is kept when such a step is deleted.
 */
export function primaryBranchCondition(type) {
  if (type === STEP_TYPES.CONNECTION_REQUEST) return CONDITION_TYPES.ACCEPTED;
  if (type === STEP_TYPES.CONDITION) return CONDITION_TYPES.CONNECTED;
  const defs = defaultBranchesFor(type);
  return (
    defs.find((b) => b.condition === CONDITION_TYPES.NO_REPLY)?.condition
    || defs[0]?.condition
    || null
  );
}

export function getSlotTarget(step, condition) {
  if (!step) return null;
  if (condition) {
    return step.branches?.find((b) => b.condition === condition)?.targetStepId ?? null;
  }
  return step.nextStepId ?? null;
}

function withSlotTarget(step, condition, targetId) {
  if (!condition) return { ...step, nextStepId: targetId };
  const branches = step.branches || [];
  const exists = branches.some((b) => b.condition === condition);
  return {
    ...step,
    branches: exists
      ? branches.map((b) => (b.condition === condition ? { ...b, targetStepId: targetId } : b))
      : [...branches, { condition, targetStepId: targetId }],
  };
}

function outgoingTargets(step) {
  const targets = [];
  if (step.branches?.length) {
    sortBranchesForDisplay(step.branches).forEach((b) => {
      if (b.targetStepId) targets.push(b.targetStepId);
    });
  } else if (step.nextStepId) {
    targets.push(step.nextStepId);
  }
  return targets;
}

/** Sort steps in flow order (depth-first from START) and refresh `order`. */
export function reorderSteps(steps) {
  const byId = new Map(steps.map((s) => [s.id, s]));
  const start = steps.find((s) => s.type === "START");
  const seen = new Set();
  const ordered = [];
  const visit = (id) => {
    const step = byId.get(id);
    if (!step || seen.has(id) || step.type === STEP_TYPES.END) return;
    seen.add(id);
    ordered.push(step);
    outgoingTargets(step).forEach(visit);
  };
  if (start) visit(start.id);
  steps.forEach((s) => {
    if (!seen.has(s.id) && s.type !== STEP_TYPES.END) ordered.push(s);
  });
  steps.forEach((s) => {
    if (s.type === STEP_TYPES.END) ordered.push(s);
  });
  return ordered.map((s, i) => ({ ...s, order: i }));
}

/**
 * Make sure a list of steps (possibly loaded from the API, which never stores START)
 * has a START node wired to the first step.
 */
/** Older steps were stored without ids; fall back to their position. */
export const stepKey = (step, index) => step?.id ?? `step_${index}`;

export function ensureSequenceShape(steps) {
  let list = (steps || []).filter(Boolean).map((s, index) => ({ ...s, id: stepKey(s, index) }));
  if (!list.length) return createEmptySequence();

  const actions = list
    .filter((s) => !isTerminalStep(s))
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  const hasLinks = list.some((s) => s.nextStepId || s.branches?.some((b) => b.targetStepId));

  // Legacy linear sequences: chain the steps by order.
  if (!hasLinks && actions.length > 1) {
    const nextById = new Map(actions.map((s, i) => [s.id, actions[i + 1]?.id ?? null]));
    list = list.map((s) => (nextById.has(s.id) ? { ...s, nextStepId: nextById.get(s.id) } : s));
  }

  if (!list.some((s) => s.type === "START")) {
    const targeted = new Set();
    list.forEach((s) => outgoingTargets(s).forEach((id) => targeted.add(id)));
    const root = actions.find((s) => !targeted.has(s.id)) || actions[0];
    list.unshift({
      id: "start",
      type: "START",
      label: "Sequence start",
      order: 0,
      nextStepId: root?.id ?? null,
    });
  }
  return reorderSteps(list);
}

/**
 * Insert `newStep` into a slot. Whatever the slot pointed at becomes the new step's
 * continuation (its primary branch if it branches); other branches start open.
 */
export function insertStepAtSlot(steps, slot, newStep) {
  const parent = steps.find((s) => s.id === slot?.parentId);
  if (!parent) return steps;
  const oldTarget = getSlotTarget(parent, slot.condition);
  const step = {
    content: "",
    delay: 0,
    delayUnit: "hours",
    ...newStep,
    id: newStep.id || createStepId(),
  };
  const defs = defaultBranchesFor(step.type);
  if (defs.length) {
    const primary = primaryBranchCondition(step.type);
    step.branches = defs.map((b) => ({
      ...b,
      targetStepId: b.condition === primary ? oldTarget : null,
    }));
    delete step.nextStepId;
  } else {
    step.nextStepId = oldTarget;
    delete step.branches;
  }
  const next = steps.map((s) =>
    s.id === parent.id ? withSlotTarget(s, slot.condition, step.id) : s,
  );
  next.push(step);
  return reorderSteps(next);
}

export function setSlotTarget(steps, slot, targetId) {
  return reorderSteps(
    steps.map((s) => (s.id === slot?.parentId ? withSlotTarget(s, slot.condition, targetId) : s)),
  );
}

/** Point a slot at the (shared) END step, creating one if needed. */
export function endSlot(steps, slot) {
  let list = steps;
  let end = list.find((s) => s.type === STEP_TYPES.END);
  if (!end) {
    end = { id: createStepId(), type: STEP_TYPES.END, label: "Sequence end" };
    list = [...list, end];
  }
  return setSlotTarget(list, slot, end.id);
}

function pruneUnreachable(steps) {
  const byId = new Map(steps.map((s) => [s.id, s]));
  const start = steps.find((s) => s.type === "START");
  const reachable = new Set();
  const stack = start ? [start.id] : [];
  while (stack.length) {
    const id = stack.pop();
    const step = byId.get(id);
    if (!step || reachable.has(id)) continue;
    reachable.add(id);
    outgoingTargets(step).forEach((t) => stack.push(t));
  }
  return steps.filter((s) => reachable.has(s.id) || s.type === STEP_TYPES.END);
}

/**
 * Delete a step. Its parent is reconnected to the step's continuation (the primary
 * branch for branching steps); steps only reachable through the other branches go too.
 */
export function removeStep(steps, stepId) {
  const target = steps.find((s) => s.id === stepId);
  if (!target || isTerminalStep(target)) return steps;
  let continuation = target.nextStepId ?? null;
  if (target.branches?.length) {
    continuation = getSlotTarget(target, primaryBranchCondition(target.type))
      ?? target.branches.find((b) => b.targetStepId)?.targetStepId
      ?? null;
  }
  if (continuation === stepId) continuation = null;

  const list = steps
    .filter((s) => s.id !== stepId)
    .map((s) => {
      let out = s;
      if (out.nextStepId === stepId) out = { ...out, nextStepId: continuation };
      if (out.branches?.some((b) => b.targetStepId === stepId)) {
        out = {
          ...out,
          branches: out.branches.map((b) =>
            b.targetStepId === stepId ? { ...b, targetStepId: continuation } : b,
          ),
        };
      }
      return out;
    });
  return reorderSteps(pruneUnreachable(list));
}

/** Steps (besides `stepId` itself) that `removeStep` would also drop. */
export function countDependentSteps(steps, stepId) {
  return Math.max(0, steps.length - removeStep(steps, stepId).length - 1);
}

/** True when a step can't run until the user configures it. */
export function stepNeedsAttention(step) {
  if (!step) return false;
  if (step.type === STEP_TYPES.MESSAGE || step.type === STEP_TYPES.IN_MAIL) {
    const hasVariant = (step.messageVariants || []).some((v) => v?.content?.trim());
    return !step.content?.trim() && !hasVariant;
  }
  if (step.type === STEP_TYPES.VOICE_NOTE) return !step.voiceFileId;
  return false;
}
